import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, onValue, get } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { useActiveWorld } from '../../hooks/useActiveWorld';

import useInboxData, { isSpecialTab } from './hooks/useInboxData';
import useMailSend from './hooks/useMailSend';
import useAiAssist from './hooks/useAiAssist';
import useAgentDrafts from './hooks/useAgentDrafts';
import useBlockContact, { getBlockTarget } from './hooks/useBlockContact';
import useAddToLeads, { resolveExistingContact, pickBusinessName } from './hooks/useAddToLeads';
import useBlockedSenders from './hooks/useBlockedSenders';
import useSafeGuardResend from './hooks/useSafeGuardResend';
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from '../../utils/cloudFunctions';
import { parseAddress } from './utils/emailParsing';

import AccountTabs from './components/AccountTabs';
import MailList from './components/MailList';
import MailDetail from './components/MailDetail';
import ConfirmDialog from './components/ConfirmDialog';
import AddToLeadsModal from './components/AddToLeadsModal';
import AgentDraftsBanner, { AgentDraftsHint } from './components/AgentDraftsBanner';
import InboxEmptyStart from './components/InboxEmptyStart';
import InlineComposer from './components/InlineComposer';
import SafeGuardResendPopup from './components/SafeGuardResendPopup';
import useTaskActionListener from '../tasks/useTaskActionListener';
import { INBOX_TASK_ACTIONS } from '../tasks/taskUtils';
import { draftForwardIntro } from '../tasks/forwardTemplateIntro';

import './InboxView.css';

const MOBILE_BREAKPOINT = 900;
const INBOX_TABS_WITH_DELETE = ['all', 'warmup']; // + specific account ids (handled below)
// Tabs where the composer is unavailable (outgoing or read-only mail), so an
// agent draft could be opened but never sent from there.
const TABS_WITHOUT_AGENT_BANNER = ['sent', 'replied', 'forwarded', 'deleted', 'scheduled'];

/**
 * InboxView — the new, self-contained two-pane (Apple Mail / Gmail style) inbox.
 *
 * This is a drop-in alternative to the classic SmartInbox. It does NOT touch
 * any classic-inbox code; it only reads the same Firestore collections and
 * reuses the same send endpoint. State is composed from small focused hooks,
 * and the UI is split into list/detail/composer components.
 */
export default function InboxView({ userId, onNavigateToSettings, onEmptyChange }) {
  const navigate = useNavigate();
  const { canWrite } = useActiveWorld();
  const db = getFirestore();
  // Postfach-Zugriffe laufen in Europa — ohne Region landet der Aufruf in Iowa
  // und der Provider protokolliert einen US-Login.
  const functions = getFunctions(undefined, 'europe-west1');

  // Persist the active tab per user so a transient parent remount (e.g. the
  // team context briefly re-resolving and unmounting this view) doesn't dump
  // the user back to "All". Restored on mount, written on every change.
  const tabStorageKey = `ivx_tab_${userId || 'anon'}`;
  const [selectedTab, setSelectedTab] = useState(() => {
    try { return sessionStorage.getItem(tabStorageKey) || 'all'; } catch (e) { return 'all'; }
  });
  const listPaneRef = useRef(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [userSettings, setUserSettings] = useState({});

  // Composer state lives here (not in MailDetail) so every path that would
  // close it can be guarded against losing unsent input.
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState('reply'); // 'reply' | 'forward' | 'new'
  const [composerDirty, setComposerDirty] = useState(false);
  // Seed text for the composer — pre-filled from a Reply-Agent draft so the
  // user only has to review and send (or clear it). The nonce is part of the
  // composer's key, so re-opening the same mail with a different draft always
  // gets a fresh composer instead of keeping the previous text.
  const [prefillText, setPrefillText] = useState('');
  const [prefillNonce, setPrefillNonce] = useState(0);
  // Recipient seeds from a Tasks-modal shortcut (new mail "to", forward "to").
  const [seedTo, setSeedTo] = useState('');
  const [seedForwardTo, setSeedForwardTo] = useState('');
  // When set, a discard-confirmation dialog is shown; `onConfirm` runs the
  // action the user was trying to take.
  const [confirm, setConfirm] = useState(null);
  // When true, the "Add to Leads" touchpoint modal is shown for the open email.
  const [addToLeadsOpen, setAddToLeadsOpen] = useState(false);
  // Agent-drafts banner: open by default (a waiting reply is the point of it),
  // but a deliberate collapse sticks for the session.
  const draftsStorageKey = `ivx_agent_open_${userId || 'anon'}`;
  const [draftsExpanded, setDraftsExpanded] = useState(() => {
    try { return sessionStorage.getItem(draftsStorageKey) !== 'collapsed'; } catch (e) { return true; }
  });
  const hintStorageKey = `ivx_agent_hint_${userId || 'anon'}`;
  const [hintDismissed, setHintDismissed] = useState(() => {
    try { return localStorage.getItem(hintStorageKey) === 'dismissed'; } catch (e) { return false; }
  });
  // Bulk Reply mode: rip through every prepared draft one after another. When
  // on, the list pane is hidden, the composer takes center stage, and each
  // successful send auto-opens the next queued draft.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkDone, setBulkDone] = useState(0);
  const [bulkTotal, setBulkTotal] = useState(0);
  // True while the enter/exit layout morph is running. Used purely for CSS
  // hooks — the iframe body gets briefly hidden during the tween so the
  // grid morph doesn't have to relayout it 60 times a second (iframes are
  // one of the slowest things a browser can reflow).
  const [bulkTransitioning, setBulkTransitioning] = useState(false);
  const bulkTransitionTimerRef = useRef(null);
  // Set by handleSend right before the async send starts, cleared by the
  // effect that auto-advances once the corresponding draft has been removed
  // from RTDB. Ref because the send flow is async and we must not stale-close
  // over the flag between the click and the eventual list update.
  const bulkAdvancePendingRef = useRef(false);
  // Preview of the existing DB contact the open email links to (or null / a
  // { matchType: null } sentinel when there's no match). Populated on open.
  const [linkedPreview, setLinkedPreview] = useState(null);
  const [linkedPreviewLoading, setLinkedPreviewLoading] = useState(false);

  // Change signal for the lead badge. The All/Leads split itself runs off the
  // denormalized `isLeadMail` flag, so the badge only needs a nudge to re-count
  // whenever this session changes a contact's lead status (add to leads, block).
  //
  // This used to be a live listener over every Lead document, which meant
  // opening the inbox downloaded the entire Leads collection purely to produce a
  // changing object identity — on a large world several MB and seconds of
  // main-thread work before anything rendered. A counter does the same job.
  // Trade-off: a lead added on ANOTHER device now shows up in the badge on the
  // next refresh instead of instantly.
  const [leadIndexVersion, setLeadIndexVersion] = useState(0);
  const invalidateLeadIndex = useCallback(() => {
    setLeadIndexVersion((v) => v + 1);
  }, []);

  const {
    accounts, accountsReady, emails, counts, loading, loadingMore, hasMore,
    error, loadMore, refresh, removeEmailLocally, patchEmailLocally,
  } = useInboxData(userId, selectedTab, leadIndexVersion);

  useEffect(() => {
    if (!onEmptyChange || !accountsReady) return undefined;
    onEmptyChange(accounts.length === 0);
    return undefined;
  }, [onEmptyChange, accountsReady, accounts.length]);

  // Which of the senders on screen are blocked (blacklisted) — used to flag
  // their mail rows with a small X so it's obvious at a glance who can no
  // longer be emailed. Resolved per visible sender: loading the full blacklist
  // here used to cost ~18 MB on every inbox open.
  const { isBlockedSender, markBlockedLocally } = useBlockedSenders(userId, emails);

  const { sending, sendReply, sendForward } = useMailSend({ userId, userSettings });
  const { generating, generateReply } = useAiAssist({ userId, userSettings });
  const { drafts: agentDrafts, dismissDraft } = useAgentDrafts(userId);
  const { blocking, blockContact } = useBlockContact(userId);
  const { addingToLeads, addToLeads, findLinkedContact, customTouchpoints } = useAddToLeads(userId);
  const { loading: resendLoading, prepareSingle, prepareAll } = useSafeGuardResend(userId);
  // { mode: 'single', rowData } | { mode: 'bulk', contacts, presetEmails }
  const [resendSession, setResendSession] = useState(null);
  const resentDraftIdsRef = useRef(new Set());

  // ── Footer/user settings (RTDB) — only as a footer fallback for sending ──
  useEffect(() => {
    if (!userId) return undefined;
    const unsub = onValue(ref(database, `/World/${userId}/settings`), (snap) => {
      if (snap.exists()) setUserSettings(snap.val());
    });
    return () => unsub();
  }, [userId]);

  // Persist the selected tab whenever it changes.
  useEffect(() => {
    try { sessionStorage.setItem(tabStorageKey, selectedTab); } catch (e) { /* storage may be unavailable */ }
  }, [tabStorageKey, selectedTab]);

  // Reset the list scroll to the top when switching tabs, so a new tab never
  // opens scrolled halfway down the previous (longer) list.
  useEffect(() => {
    if (listPaneRef.current) listPaneRef.current.scrollTop = 0;
  }, [selectedTab]);

  // ── Responsive ───────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Also guard against an accidental browser reload / tab close while there is
  // unsent input (native confirmation — the only thing browsers allow here).
  useEffect(() => {
    if (!(composerOpen && composerDirty)) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [composerOpen, composerDirty]);

  const showToast = useCallback((message, kind = 'info') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Server-side IMAP fetch (manual only) ─────────────────────────
  // The data hook only *reads* Firestore. A freshly received email only
  // lands in Firestore once the server pulls it from the mailbox via IMAP.
  // A scheduled background job already does this continuously, so opening the
  // inbox does NOT need to trigger an IMAP pull — the list loads straight from
  // Firestore (fast). This manual fetch is wired only to the Refresh button,
  // for when the user explicitly wants to pull right now.
  const fetchFromServer = useCallback(async ({ silent = false } = {}) => {
    if (!userId) return;
    setFetching(true);
    try {
      const manualFetch = httpsCallable(functions, 'manualFetchSmartDomainEmails');
      await manualFetch({ userId });
    } catch (err) {
      console.warn('[InboxView] manual IMAP fetch failed:', err?.message);
      if (!silent) showToast('Could not fetch new emails right now.', 'error');
    } finally {
      // Always reload from Firestore — even if the fetch failed, the
      // scheduled background job may have added mail in the meantime.
      await refresh();
      setFetching(false);
    }
  }, [userId, functions, showToast, refresh]);

  // ── Discard guard ────────────────────────────────────────────────
  // Fully closes the composer and clears its dirty flag + any pre-fill.
  const closeComposer = useCallback(() => {
    setComposerOpen(false);
    setComposerDirty(false);
    setComposerMode('reply');
    setPrefillText('');
    setSeedTo('');
    setSeedForwardTo('');
  }, []);

  // Runs `proceed` immediately, unless there is unsent input — then it asks the
  // user to confirm first so a stray click can't throw their draft away.
  const guardDiscard = useCallback((proceed) => {
    if (composerOpen && composerDirty) {
      setConfirm({ onConfirm: proceed });
    } else {
      proceed();
    }
  }, [composerOpen, composerDirty]);

  // Loads a drafted reply into the composer and opens it.
  const applyPrefill = useCallback((text) => {
    setPrefillText(text);
    setPrefillNonce((n) => n + 1);
    setComposerMode('reply');
    setComposerOpen(true);
  }, []);

  // ── Selection (marks inbox mail as read, best effort) ────────────
  const doSelect = useCallback(async (email, presetDraft) => {
    closeComposer();
    setSelectedEmail(email);

    // If a Reply-Agent already drafted an answer for this mail (auto-process
    // stored it in RTDB), pre-fill the composer with it so the user only has
    // to review + send. Coming from the banner the text is already at hand;
    // otherwise it's fetched. Fire-and-forget; never blocks selection.
    if (presetDraft) {
      applyPrefill(presetDraft);
    } else if (email?.hasDraft && email?.draftId && email?.agentAction === 'draft_created') {
      get(ref(database, `/World/${userId}/emailDrafts/${email.draftId}`))
        .then((snap) => {
          const draft = snap.exists() ? snap.val() : null;
          if (draft?.draftContent) applyPrefill(draft.draftContent);
        })
        .catch((e) => console.warn('[InboxView] agent draft load failed:', e?.message));
    }

    const isInboxMail = !isSpecialTab(selectedTab) || selectedTab === 'warmup' || selectedTab === 'all';
    if (isInboxMail && email.isRead === false && email.isSent !== true) {
      patchEmailLocally(email.id, { isRead: true });
      try {
        const markRead = httpsCallable(functions, 'markEmailAsRead');
        await markRead({ emailId: email.id, userId });
      } catch (err) {
        // Non-fatal: UI already updated optimistically.
        console.warn('[InboxView] markEmailAsRead failed:', err?.message);
      }
    }
  }, [selectedTab, functions, userId, patchEmailLocally, closeComposer, applyPrefill]);

  const handleSelect = useCallback((email) => {
    // Re-selecting the already-open mail must not disturb an active composer.
    if (email?.id && email.id === selectedEmail?.id) return;
    guardDiscard(() => doSelect(email));
  }, [selectedEmail, guardDiscard, doSelect]);

  // Switching tabs clears the open email; guard it too.
  const handleSelectTab = useCallback((tab) => {
    if (tab === selectedTab) return;
    guardDiscard(() => {
      closeComposer();
      setSelectedEmail(null);
      setSelectedTab(tab);
      // Bulk mode belongs to the drafts queue; switching tabs implies the
      // user wants out of the treadmill and back to normal browsing.
      setBulkMode(false);
      setBulkDone(0);
      setBulkTotal(0);
      bulkAdvancePendingRef.current = false;
    });
  }, [selectedTab, guardDiscard, closeComposer]);

  // Mobile "back" returns to the list, dropping the detail (and composer).
  const handleBack = useCallback(() => {
    guardDiscard(() => {
      closeComposer();
      setSelectedEmail(null);
    });
  }, [guardDiscard, closeComposer]);

  // ── Composer open/close handlers ─────────────────────────────────
  const handleOpenComposer = useCallback(() => {
    setComposerMode('reply');
    setComposerOpen(true);
  }, []);

  // "New Email" opens the same composer without a source mail, in the detail
  // pane. It shares the composer state so every discard guard applies.
  const isNewEmail = composerOpen && composerMode === 'new';
  const handleNewEmail = useCallback(() => {
    guardDiscard(() => {
      closeComposer();
      setSelectedEmail(null);
      setComposerMode('new');
      setComposerOpen(true);
    });
  }, [guardDiscard, closeComposer]);

  // Tasks-page shortcuts: write a fresh mail to the contact, or forward a
  // chosen conversation to someone new at the company (blank or with a
  // template-drafted cover text).
  const handleTaskShortcut = useCallback((request) => {
    guardDiscard(async () => {
      if (request.kind === 'new_mail') {
        closeComposer();
        setSelectedEmail(null);
        setSeedTo(request.to || '');
        setPrefillNonce((n) => n + 1);
        setComposerMode('new');
        setComposerOpen(true);
        return;
      }
      // Forward: the conversation was picked explicitly on the tasks page, so
      // load exactly that mail — never guess the "latest" one.
      if (!request.emailId) return;
      try {
        const snap = await getDoc(doc(db, 'world_inbox', userId, 'emails', request.emailId));
        if (!snap.exists() || snap.data().deleted) {
          showToast('This conversation is no longer in your inbox.', 'error');
          return;
        }
        const email = { id: snap.id, ...snap.data(), hasDraft: false };
        if (request.kind === 'reply_thread') {
          doSelect(email);
          setPrefillText('');
          setPrefillNonce((n) => n + 1);
          setComposerMode('reply');
          setComposerOpen(true);
          return;
        }
        let intro = '';
        if (request.templateId) {
          showToast('Writing the cover text from your template…', 'success');
          const account = accounts.find((a) => a.id === email.accountId)
            || accounts.find((a) => a.email === email.accountEmail)
            || accounts[0];
          try {
            intro = await draftForwardIntro({
              userId,
              templateId: request.templateId,
              email,
              forwardTo: request.forwardTo,
              forwardToName: request.forwardToName,
              account,
              userSettings,
            });
          } catch (err) {
            showToast(`${err.message} Opened the forward without text.`, 'error');
          }
        }
        doSelect(email);
        setSeedForwardTo(request.forwardTo || '');
        setPrefillText(intro);
        setPrefillNonce((n) => n + 1);
        setComposerMode('forward');
        setComposerOpen(true);
      } catch (err) {
        console.error('[InboxView] task forward lookup failed:', err);
        showToast('Could not load the conversation.', 'error');
      }
    });
  }, [guardDiscard, closeComposer, db, userId, doSelect, showToast, accounts, userSettings]);

  useTaskActionListener(INBOX_TASK_ACTIONS, handleTaskShortcut, !!userId);

  const checkNewRecipient = useCallback(async (emailAddress) => {
    const match = await resolveExistingContact(db, userId, emailAddress);
    if (!match?.matchType) return null;
    return {
      matchType: match.matchType,
      docId: match.docId,
      domain: match.domain || '',
      name: pickBusinessName(match.data || {}, emailAddress),
    };
  }, [db, userId]);

  // Forward opens the same composer in "forward" mode (editable destination,
  // original message embedded). Guard any unsent reply first.
  const handleForward = useCallback(() => {
    if (!selectedEmail) return;
    guardDiscard(() => {
      setPrefillText('');
      setComposerMode('forward');
      setComposerOpen(true);
    });
  }, [selectedEmail, guardDiscard]);
  // The X icon is easy to misclick → guard it.
  const handleRequestCloseComposer = useCallback(() => {
    // In Bulk Reply mode the composer's "X" doubles as an exit shortcut:
    // closing the composer without either sending or explicitly skipping
    // would leave the user parked on a single mail with no way to continue
    // the treadmill, which is the opposite of the mode's intent.
    if (bulkMode) {
      guardDiscard(() => {
        closeComposer();
        setBulkMode(false);
        setBulkDone(0);
        setBulkTotal(0);
        bulkAdvancePendingRef.current = false;
      });
      return;
    }
    guardDiscard(() => closeComposer());
  }, [bulkMode, guardDiscard, closeComposer]);
  // The explicit "Discard" button is an intentional escape hatch → no prompt.
  // In Bulk Reply mode a discard means "skip this prepared reply and jump to
  // the next one", so we also drop the RTDB draft and let the auto-advance
  // effect open the following draft (or wrap up if the queue is empty).
  const handleForceCloseComposer = useCallback(() => {
    if (bulkMode) {
      const skippedEmailId = selectedEmail?.id || null;
      // Trust the LIVE drafts queue over the mail doc: the mail's cached
      // draftId can drift out of sync (e.g. a re-drafted mail), so looking
      // the draft up by originalEmailId is the only reliable way to hit
      // the exact RTDB entry we want to remove. Falling back to the mail's
      // own draftId only if the queue has nothing.
      const liveDraft = skippedEmailId
        ? agentDrafts.find((d) => d.originalEmailId === skippedEmailId)
        : null;
      const skippedDraftId = liveDraft?.id || selectedEmail?.draftId || null;
      closeComposer();
      if (skippedEmailId) {
        patchEmailLocally(skippedEmailId, { hasDraft: false, draftId: null });
      }
      setBulkDone((n) => n + 1);
      bulkAdvancePendingRef.current = true;
      if (skippedDraftId) {
        // Removes it from RTDB AND hides it locally straight away — the
        // advance effect then fires as soon as `agentDrafts` recomputes.
        dismissDraft(skippedDraftId);
      }
      return;
    }
    closeComposer();
  }, [bulkMode, selectedEmail, agentDrafts, closeComposer, patchEmailLocally, dismissDraft]);

  // ── Delete (mark inbox doc deleted) ──────────────────────────────
  // Scheduled mail isn't an inbox doc — deleting it means cancelling the
  // pending send (Cloud Task) via the same backend the classic inbox used.
  const isScheduledTab = selectedTab === 'scheduled';
  const canDelete = isScheduledTab
    || INBOX_TABS_WITH_DELETE.includes(selectedTab)
    || (!isSpecialTab(selectedTab)); // specific account ids are inbox views too

  const doDelete = useCallback(async () => {
    if (!selectedEmail) return;
    setDeleting(true);
    try {
      const emailRef = doc(db, 'world_inbox', userId, 'emails', selectedEmail.id);
      await updateDoc(emailRef, { deleted: true, deletedAt: Timestamp.now() });
      // A drafted reply to a deleted mail is dead weight — drop it with the mail
      // instead of leaving it in the agent banner.
      if (selectedEmail.draftId) dismissDraft(selectedEmail.draftId);
      removeEmailLocally(selectedEmail.id);
      closeComposer();
      setSelectedEmail(null);
      refresh();
    } catch (err) {
      console.error('[InboxView] delete failed:', err);
      showToast('Could not delete email.', 'error');
    } finally {
      setDeleting(false);
    }
  }, [selectedEmail, db, userId, removeEmailLocally, refresh, showToast, closeComposer, dismissDraft]);

  // Cancel a scheduled (not-yet-sent) email: removes the Cloud Task and the
  // Firestore doc server-side, then drops it from the list optimistically.
  const doCancelScheduled = useCallback(async () => {
    if (!selectedEmail) return;
    setDeleting(true);
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.cancelScheduledEmail(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, scheduledEmailId: selectedEmail.id }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.details || result.error || `HTTP ${response.status}`);
      }

      removeEmailLocally(selectedEmail.id);
      closeComposer();
      setSelectedEmail(null);
      refresh();
      showToast('Scheduled email deleted.', 'success');
    } catch (err) {
      console.error('[InboxView] cancel scheduled failed:', err);
      const msg = String(err?.message || '');
      const friendly = msg.includes('404')
        ? 'Email not found — it may have already been sent or deleted.'
        : 'Could not delete the scheduled email.';
      showToast(friendly, 'error');
    } finally {
      setDeleting(false);
    }
  }, [selectedEmail, userId, removeEmailLocally, refresh, showToast, closeComposer]);

  // Send a scheduled email immediately (before its planned time). The backend
  // deletes the Cloud Task first, so the mail can never go out twice.
  const doSendNow = useCallback(async () => {
    if (!selectedEmail) return;
    setSendingNow(true);
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendScheduledEmailNow(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, scheduledEmailId: selectedEmail.id }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        const reasonCode = result?.error || `HTTP_${response.status}`;
        let userMsg = result?.details || 'Could not send email now.';
        if (reasonCode === 'already_in_progress') {
          userMsg = 'This email is already being sent — please wait a moment.';
        } else if (reasonCode === 'already_sent') {
          userMsg = 'This email has already been sent.';
        } else if (reasonCode === 'cloud_task_not_found' || reasonCode === 'cloud_task_body_unavailable') {
          userMsg = 'The scheduled email is no longer in the queue (it may have already been sent).';
        } else if (reasonCode === 'no_cloud_task') {
          userMsg = 'This scheduled email is too old to trigger early. Please delete and resend manually.';
        }
        throw new Error(userMsg);
      }

      removeEmailLocally(selectedEmail.id);
      closeComposer();
      setSelectedEmail(null);
      refresh();
      showToast('Email sent.', 'success');
    } catch (err) {
      console.error('[InboxView] send-now failed:', err);
      showToast(err?.message || 'Could not send email now.', 'error');
    } finally {
      setSendingNow(false);
    }
  }, [selectedEmail, userId, removeEmailLocally, refresh, showToast, closeComposer]);

  const handleSendNow = useCallback(() => {
    if (!selectedEmail) return;
    const recipient = selectedEmail.recipientEmail || selectedEmail.to || 'this contact';
    setConfirm({
      title: 'Send now?',
      message: `This sends the email to ${recipient} immediately instead of at its scheduled time. This cannot be undone.`,
      confirmLabel: 'Send now',
      cancelLabel: 'Cancel',
      onConfirm: doSendNow,
    });
  }, [selectedEmail, doSendNow]);

  const handleDelete = useCallback(() => {
    if (isScheduledTab) {
      const recipient = selectedEmail?.recipientEmail || selectedEmail?.to || 'this contact';
      setConfirm({
        title: 'Delete scheduled email?',
        message: `This removes the email queued for ${recipient} from the send queue. It will not be sent. This cannot be undone.`,
        confirmLabel: 'Delete email',
        cancelLabel: 'Keep',
        onConfirm: doCancelScheduled,
      });
      return;
    }
    guardDiscard(() => doDelete());
  }, [isScheduledTab, selectedEmail, guardDiscard, doDelete, doCancelScheduled]);

  // ── SafeGuard: send parked mail as it was meant to go out ────────
  // One mail opens in Normal mode, "all" in Bulk mode; both prefilled from
  // the draft and sent as approved, so SafeGuard doesn't park them again.
  const isSafeGuardTab = selectedTab === 'safeguard';

  const handleResendSafeGuard = useCallback(async () => {
    if (!selectedEmail?.isSafeGuardDraft) return;
    const { rowData, error } = await prepareSingle(selectedEmail);
    if (error) {
      showToast(error, 'error');
      return;
    }
    resentDraftIdsRef.current = new Set();
    setResendSession({ mode: 'single', rowData });
  }, [selectedEmail, prepareSingle, showToast]);

  const handleResendAllSafeGuard = useCallback(async () => {
    const { contacts, presetEmails, skipped, error } = await prepareAll();
    if (error) {
      showToast(error, 'error');
      return;
    }
    if (skipped > 0) {
      showToast(`${skipped} SafeGuard mail${skipped === 1 ? '' : 's'} left out (older duplicate per contact, CRM/invalid address, or missing contact/content).`, 'info');
    }
    resentDraftIdsRef.current = new Set();
    setResendSession({ mode: 'bulk', contacts, presetEmails });
  }, [prepareAll, showToast]);

  const handleResendResult = useCallback((status, message, contact) => {
    if (status === 'success') {
      const draftId = resendSession?.mode === 'single'
        ? resendSession.rowData?.resendDraft?.draftId
        : resendSession?.presetEmails?.[contact?.id]?.draftId;
      if (draftId) resentDraftIdsRef.current.add(draftId);
      return;
    }
    if (typeof message === 'string' && ['error', 'blocked', 'warning', 'safeguard_rejected', 'scorer_rejected'].includes(status)) {
      showToast(message, 'error');
    }
  }, [resendSession, showToast]);

  const handleCloseResend = useCallback(() => {
    const sent = resentDraftIdsRef.current;
    sent.forEach((id) => removeEmailLocally(id));
    if (selectedEmail && sent.has(selectedEmail.id)) setSelectedEmail(null);
    if (sent.size > 0) {
      showToast(`${sent.size} mail${sent.size === 1 ? '' : 's'} sent from SafeGuard.`, 'success');
    }
    resentDraftIdsRef.current = new Set();
    setResendSession(null);
    // Bulk sends finish in the background; give the draft deletes a moment
    // before re-reading the tab.
    setTimeout(() => refresh(), 3000);
  }, [selectedEmail, removeEmailLocally, refresh, showToast]);

  // ── Block contact (domain-level blacklist) ───────────────────────
  // Mirrors the classic SmartInbox: custom domains block the whole domain,
  // consumer providers (gmail, gmx…) block only the single address. The
  // blacklist is enforced server-side so blocked senders can't be emailed.
  const doBlock = useCallback(async () => {
    if (!selectedEmail) return;
    const result = await blockContact(selectedEmail);
    if (result.success) {
      // Never keep offering a ready-to-send reply to someone we just blocked.
      if (selectedEmail.draftId) dismissDraft(selectedEmail.draftId);
      showToast(result.message, 'success');
      // We know what was just written, so the x can appear without asking the
      // server again.
      if (result.blockEntry) markBlockedLocally(result.blockEntry);
      // Blocking flips matching contacts to "Blacklisted", so a sender that was
      // a Lead no longer is — the lead badge has to re-count.
      invalidateLeadIndex();
      refresh();
    } else {
      showToast(result.message || 'Could not block contact.', 'error');
    }
  }, [selectedEmail, blockContact, showToast, refresh, dismissDraft, invalidateLeadIndex, markBlockedLocally]);

  const handleBlock = useCallback(() => {
    if (!selectedEmail) return;
    const target = getBlockTarget(selectedEmail.from);
    if (!target) {
      showToast('No valid sender address found in this email.', 'error');
      return;
    }
    const message = target.blockType === 'domain'
      ? `This blocks the entire domain ${target.domain}. Every contact from this domain is marked Not Qualified and can no longer be emailed.`
      : `This blocks ${target.emailAddress}. The contact is marked Not Qualified and can no longer be emailed.`;
    setConfirm({
      title: 'Block contact?',
      message,
      confirmLabel: target.blockType === 'domain' ? 'Block domain' : 'Block contact',
      cancelLabel: 'Cancel',
      onConfirm: doBlock,
    });
  }, [selectedEmail, showToast, doBlock]);

  // ── Add to Leads (World DB) ──────────────────────────────────────
  // Mirrors the classic SmartInbox: opens a touchpoint-selection modal, then
  // writes the sender into World/{uid}/db with status "Lead" so it shows up in
  // the World DB "Leads" tab and can be managed from there.
  const handleOpenAddToLeads = useCallback(() => {
    if (!selectedEmail) return;
    setLinkedPreview(null);
    setLinkedPreviewLoading(true);
    setAddToLeadsOpen(true);
    findLinkedContact(selectedEmail)
      .then((preview) => setLinkedPreview(preview))
      .catch(() => setLinkedPreview({ matchType: null }))
      .finally(() => setLinkedPreviewLoading(false));
  }, [selectedEmail, findLinkedContact]);

  const handleCloseAddToLeads = useCallback(() => {
    setAddToLeadsOpen(false);
    setLinkedPreview(null);
    setLinkedPreviewLoading(false);
  }, []);

  const handleConfirmAddToLeads = useCallback(async (touchpoints) => {
    if (!selectedEmail) return;
    const result = await addToLeads(selectedEmail, touchpoints);
    handleCloseAddToLeads();
    if (result.success) {
      showToast(result.message, 'success');
      invalidateLeadIndex();
    } else {
      showToast(result.message || 'Could not add contact to Leads.', 'error');
    }
  }, [selectedEmail, addToLeads, showToast, handleCloseAddToLeads, invalidateLeadIndex]);

  // ── Agent drafts banner ──────────────────────────────────────────
  // Opening a card selects the mail the draft answers and loads the text into
  // the composer, so the reply is always reviewed next to its original.
  const handleOpenDraft = useCallback(async (draft) => {
    const emailId = draft?.originalEmailId;
    if (!emailId) return;
    const text = draft.draftContent || '';

    if (selectedEmail?.id === emailId) {
      guardDiscard(() => applyPrefill(text));
      return;
    }

    const loaded = emails.find((e) => e.id === emailId);
    if (loaded) {
      guardDiscard(() => doSelect(loaded, text));
      return;
    }

    // The mail isn't on the current page (or tab) — fetch it directly so the
    // draft stays reachable regardless of where the user is in the list.
    try {
      const snap = await getDoc(doc(db, 'world_inbox', userId, 'emails', emailId));
      if (!snap.exists()) {
        showToast('The original email is no longer available.', 'error');
        return;
      }
      guardDiscard(() => doSelect({ id: snap.id, ...snap.data() }, text));
    } catch (err) {
      console.warn('[InboxView] opening agent draft failed:', err?.message);
      showToast('Could not open the original email.', 'error');
    }
  }, [selectedEmail, emails, db, userId, guardDiscard, doSelect, applyPrefill, showToast]);

  // Discarding a draft also clears the mail's flag, so the list row stops
  // advertising a reply that no longer exists.
  const handleDismissDraft = useCallback(async (draft) => {
    if (!draft?.id) return;
    await dismissDraft(draft.id);
    if (!draft.originalEmailId) return;
    patchEmailLocally(draft.originalEmailId, { hasDraft: false, draftId: null });
    try {
      await updateDoc(doc(db, 'world_inbox', userId, 'emails', draft.originalEmailId), {
        hasDraft: false,
        agentAction: 'draft_discarded',
      });
    } catch (err) {
      console.warn('[InboxView] clearing draft flag failed:', err?.message);
    }
  }, [dismissDraft, patchEmailLocally, db, userId]);

  const handleToggleDrafts = useCallback(() => {
    setDraftsExpanded((open) => {
      const next = !open;
      try { sessionStorage.setItem(draftsStorageKey, next ? 'open' : 'collapsed'); } catch (e) { /* storage may be unavailable */ }
      return next;
    });
  }, [draftsStorageKey]);

  // ── Bulk Reply mode ──────────────────────────────────────────────
  // Enter: opens the newest queued draft and puts the UI into "reply
  // treadmill" layout (list pane hidden, composer takes center stage). Exit:
  // resets counters — the composer stays open on whatever the user has open,
  // so a half-typed reply is never thrown away.
  // Duration of the CSS grid + topbar tween. Kept in JS so we can lift the
  // iframe hide-shield right when the animation finishes, not a frame early
  // (would flash a reflow) and not late (would leave the mail body dimmed).
  const BULK_TRANSITION_MS = 600;

  const beginBulkTween = useCallback(() => {
    setBulkTransitioning(true);
    if (bulkTransitionTimerRef.current) clearTimeout(bulkTransitionTimerRef.current);
    bulkTransitionTimerRef.current = setTimeout(() => {
      setBulkTransitioning(false);
      bulkTransitionTimerRef.current = null;
    }, BULK_TRANSITION_MS);
  }, []);

  useEffect(() => () => {
    if (bulkTransitionTimerRef.current) clearTimeout(bulkTransitionTimerRef.current);
  }, []);

  const enterBulkMode = useCallback(() => {
    const first = agentDrafts[0];
    if (!first) return;
    setBulkTotal(agentDrafts.length);
    setBulkDone(0);
    bulkAdvancePendingRef.current = false;
    // Two-stage entrance: mount the mail + composer FIRST (in the still-
    // normal layout), then let the browser paint that frame, THEN flip on
    // Bulk Mode so the CSS transitions run on an already-hot DOM tree.
    // Doing both in the same tick forces React to mount MailDetail while
    // the topbar collapse + grid morph are ALSO in flight, which is what
    // caused the entrance to feel choppy.
    handleOpenDraft(first);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        beginBulkTween();
        setBulkMode(true);
      });
    });
  }, [agentDrafts, handleOpenDraft, beginBulkTween]);

  const exitBulkMode = useCallback(() => {
    beginBulkTween();
    setBulkMode(false);
    setBulkDone(0);
    setBulkTotal(0);
    bulkAdvancePendingRef.current = false;
  }, [beginBulkTween]);

  // Once the just-sent draft has actually vanished from RTDB, advance to the
  // next queued draft — or wrap up if the queue is empty. The RTDB update is
  // async, so we can't jump straight from onSuccess: we have to wait for
  // agentDrafts to reflect the removal, otherwise the "next" pick could still
  // be the mail we just answered.
  useEffect(() => {
    if (!bulkMode || !bulkAdvancePendingRef.current) return;
    const currentStillPending = selectedEmail?.id
      && agentDrafts.some((d) => d.originalEmailId === selectedEmail.id);
    if (currentStillPending) return;

    bulkAdvancePendingRef.current = false;
    const next = agentDrafts[0];
    if (next) {
      handleOpenDraft(next);
    } else {
      setBulkMode(false);
      showToast('Bulk Reply finished — all prepared replies handled.', 'success');
    }
  // handleOpenDraft is referentially stable enough for this pass; adding it
  // would create a loop because it closes over emails/selectedEmail.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentDrafts, bulkMode, selectedEmail?.id]);

  const handleDismissHint = useCallback(() => {
    setHintDismissed(true);
    try { localStorage.setItem(hintStorageKey, 'dismissed'); } catch (e) { /* storage may be unavailable */ }
  }, [hintStorageKey]);

  // ── Send + AI assist wrappers ────────────────────────────────────
  // The send itself runs in the global InboxSendJobs registry (see
  // contexts/InboxSendJobsContext + components/InboxSendingBar), so the HTTP
  // request keeps running — and its progress stays visible in the bottom-right
  // corner — even if the user navigates away from the inbox mid-send. We
  // close the composer immediately so the UI feels instant; the mail row is
  // patched to `isReplied: true` once the send actually completes.
  const handleSend = useCallback((args) => {
    const isForward = args?.mode === 'forward';
    const isNew = args?.mode === 'new';
    // Snapshot the currently open email so post-send callbacks patch the
    // right row even if the user moved on to a different mail in the meantime.
    const targetEmail = selectedEmail;
    // Prefer the live RTDB draft id (matched by originalEmailId) over the
    // one cached on the mail doc — the two can drift out of sync when the
    // reply agent re-drafts a mail, and using the stale id silently removes
    // the wrong RTDB path (or nothing at all), keeping the card in the
    // banner forever and stalling Bulk Reply.
    const liveDraftForTarget = targetEmail?.id
      ? agentDrafts.find((d) => d.originalEmailId === targetEmail.id)
      : null;
    const targetDraftId = liveDraftForTarget?.id || targetEmail?.draftId || null;
    // Bulk mode only advances on a successful reply — a forward never
    // consumes a draft, so it must not tick the counter.
    const advanceBulk = bulkMode && !isForward;

    const startJob = isForward ? sendForward : sendReply;
    const jobId = startJob({
      ...args,
      isNewEmail: isNew,
      onSuccess: (result) => {
        if (isForward) {
          // A forward never alters the original's reply state.
          showToast(result.drafted ? result.message : 'Email forwarded.', result.drafted ? 'info' : 'success');
        } else {
          // In bulk mode the per-send toast would fire N times in a row and
          // drown out the final summary — the progress bar in the banner is
          // enough feedback while the treadmill is running.
          if (!bulkMode) {
            showToast(result.drafted ? result.message : (isNew ? 'Email sent.' : 'Reply sent.'), result.drafted ? 'info' : 'success');
          }
          if (!result.drafted && targetEmail?.id) {
            patchEmailLocally(targetEmail.id, { isReplied: true, isRead: true });

            // Replying from here supersedes any pending Reply-Agent draft for
            // this mail — drop it from RTDB so it no longer shows up in the
            // classic drafts / review queue (reads /World/{uid}/emailDrafts).
            if (targetDraftId) {
              patchEmailLocally(targetEmail.id, { hasDraft: false, draftId: null });
              // dismissDraft covers both the RTDB removal and the immediate
              // local hide — remove() alone leaves the card visible for the
              // roundtrip, which is enough to visibly stall Bulk Reply.
              dismissDraft(targetDraftId);
            }
            if (advanceBulk) {
              setBulkDone((n) => n + 1);
              bulkAdvancePendingRef.current = true;
            }
          }
        }
        refresh();
      },
      onError: (result) => {
        // The corner sending bar shows the error itself; the toast is a
        // duplicate signal for users still on the inbox screen.
        showToast(result?.message || 'Send failed.', 'error');
        // A failed send in bulk mode must not silently advance past the
        // draft the user still needs to fix.
        if (advanceBulk) bulkAdvancePendingRef.current = false;
      },
    });

    // If the job was rejected before enqueue (e.g. no account resolved), the
    // composer must stay open so the user can fix it — the onError callback
    // already showed the toast.
    if (jobId) closeComposer();
  }, [sendReply, sendForward, showToast, refresh, selectedEmail, patchEmailLocally, closeComposer, userId, bulkMode, agentDrafts, dismissDraft]);

  // Desktop keeps the list pane mounted even in Bulk Reply — its grid
  // column animates from 260px → 0 rather than the pane popping in/out, so
  // the transition into (and out of) bulk feels like a single smooth slide
  // instead of an abrupt layout swap. Mobile still hides the list once a
  // mail is open, since it's a single-column layout to begin with.
  const showList = !isMobile || (!selectedEmail && !bulkMode && !isNewEmail);
  const showDetail = !isMobile || !!selectedEmail || bulkMode || isNewEmail;

  // The banner is hidden on tabs where the composer isn't available, so a card
  // can never lead to a mail that can't be answered.
  const agentBannerAllowed = !TABS_WITHOUT_AGENT_BANNER.includes(selectedTab);
  const showAgentDrafts = agentBannerAllowed && (agentDrafts.length > 0 || bulkMode);
  // Auto-collapse the drafts strip whenever the composer is open — the
  // preview cards eat so much vertical space that the reply itself is barely
  // visible otherwise. The user's manual expand/collapse choice is preserved,
  // so closing the composer restores whatever they had before.
  const effectiveDraftsExpanded = draftsExpanded && !composerOpen && !bulkMode;
  // Only worth a nudge when agents exist but never get to run — otherwise an
  // empty inbox agent means simply "nothing to reply to yet", which needs no UI.
  const showAgentHint = agentBannerAllowed
    && !showAgentDrafts
    && !hintDismissed
    && userSettings.autoProcessEmails !== true
    && !!userSettings.agents
    && Object.keys(userSettings.agents).length > 0;

  if (!accountsReady) {
    return <div className="ivx-root" aria-busy="true" />;
  }

  if (accounts.length === 0) {
    return (
      <InboxEmptyStart
        canWrite={canWrite()}
        onConnectAccount={() => navigate('/world?view=settings&tab=email')}
      />
    );
  }

  return (
    <div className={`ivx-root ${bulkMode ? 'bulk' : ''} ${bulkTransitioning ? 'bulk-tween' : ''}`}>
      <div className={`ivx-topbar ${bulkMode ? 'ivx-topbar-hidden' : ''}`}>
        <div className="ivx-topbar-lead">
          <h2 className="ivx-title">Inbox</h2>
          {counts.unreadPrimary > 0 && (
            <span className="ivx-unread-pill" title={`${counts.unreadPrimary} unread (bounce notices excluded)`}>{counts.unreadPrimary}</span>
          )}
        </div>

        <AccountTabs
          selectedTab={selectedTab}
          onSelectTab={handleSelectTab}
          accounts={accounts}
          counts={counts}
        />

        <button
          type="button"
          className="ivx-refresh ivx-refresh-icononly"
          onClick={() => fetchFromServer()}
          disabled={fetching}
          title={fetching ? 'Refreshing…' : 'Fetch new emails from the server'}
          aria-label="Refresh"
        >
          <svg className={`ivx-refresh-icon ${fetching ? 'spinning' : ''}`} width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M20 12a8 8 0 10-2.3 5.6M20 12V7m0 5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {canWrite() && (
          <button
            type="button"
            className="ivx-refresh ivx-newmail-btn"
            onClick={handleNewEmail}
            title="Write a new email"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            New Email
          </button>
        )}
      </div>

      {error && <div className="ivx-error">{error}</div>}

      {showAgentDrafts && (
        <AgentDraftsBanner
          drafts={agentDrafts}
          expanded={effectiveDraftsExpanded}
          onToggle={handleToggleDrafts}
          onOpenDraft={handleOpenDraft}
          onDismissDraft={handleDismissDraft}
          activeEmailId={selectedEmail?.id}
          bulkMode={bulkMode}
          bulkTotal={bulkTotal}
          bulkDone={bulkDone}
          onToggleBulk={enterBulkMode}
          onExitBulk={exitBulkMode}
        />
      )}

      {showAgentHint && (
        <AgentDraftsHint
          onOpenSettings={onNavigateToSettings}
          onDismiss={handleDismissHint}
        />
      )}

      <div className={`ivx-panes ${isMobile ? 'mobile' : ''} ${bulkMode ? 'bulk' : ''}`}>
        {showList && (
          <div
            className={`ivx-pane ivx-pane-list ${bulkMode ? 'ivx-pane-list-hidden' : ''}`}
            ref={listPaneRef}
          >
            {isSafeGuardTab && canWrite() && counts.safeguard > 0 && (
              <div className="ivx-sg-bar">
                <span className="ivx-sg-bar-text">
                  {counts.safeguard} parked mail{counts.safeguard === 1 ? '' : 's'}
                </span>
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={handleResendAllSafeGuard}
                  disabled={resendLoading}
                  title="Open all parked mails in Bulk mode, prefilled as they were meant to go out"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M3 11l18-8-8 18-2-7-8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  {resendLoading ? 'Loading…' : 'Send all as intended'}
                </button>
              </div>
            )}
            <MailList
              emails={emails}
              selectedTab={selectedTab}
              selectedEmailId={selectedEmail?.id}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              onSelect={handleSelect}
              onLoadMore={loadMore}
              isBlockedSender={isBlockedSender}
            />
          </div>
        )}

        {showDetail && (
          <div className={`ivx-pane ivx-pane-detail ${bulkMode ? 'bulk' : ''}`}>
            {isNewEmail ? (
              <div className="ivx-newmail">
                <InlineComposer
                  key={`new-${prefillNonce}`}
                  userId={userId}
                  originalEmail={null}
                  initialTo={seedTo}
                  accounts={accounts}
                  sending={sending}
                  generating={false}
                  mode="new"
                  onSend={handleSend}
                  onGenerate={() => null}
                  onRequestClose={handleRequestCloseComposer}
                  onForceClose={handleForceCloseComposer}
                  onDirtyChange={setComposerDirty}
                  onCheckRecipient={checkNewRecipient}
                />
              </div>
            ) : (
            <MailDetail
              key={selectedEmail?.id || 'empty'}
              userId={userId}
              email={selectedEmail}
              selectedTab={selectedTab}
              accounts={accounts}
              sending={sending}
              generating={generating}
              composerOpen={composerOpen}
              composerMode={composerMode}
              composerInitialText={prefillText}
              composerSeed={prefillNonce}
              composerForwardTo={seedForwardTo}
              composerForwardScope={seedForwardTo ? 'thread' : 'single'}
              onOpenComposer={handleOpenComposer}
              onRequestCloseComposer={handleRequestCloseComposer}
              onForceCloseComposer={handleForceCloseComposer}
              onComposerDirtyChange={setComposerDirty}
              onSend={handleSend}
              onGenerate={generateReply}
              onDelete={canDelete ? handleDelete : null}
              deleteLabel={isScheduledTab ? 'Delete scheduled email' : 'Delete'}
              onSendNow={isScheduledTab ? handleSendNow : null}
              sendingNow={sendingNow}
              onResendAsIntended={isSafeGuardTab && canWrite() ? handleResendSafeGuard : null}
              onResendAll={isSafeGuardTab && canWrite() ? handleResendAllSafeGuard : null}
              resendLoading={resendLoading}
              onBlock={handleBlock}
              onAddToLeads={handleOpenAddToLeads}
              onForward={handleForward}
              onClose={handleBack}
              onNotify={showToast}
              deleting={deleting}
              blocking={blocking}
              isMobile={isMobile}
            />
            )}
          </div>
        )}
      </div>

      {toast && (
        <div className={`ivx-toast ivx-toast-${toast.kind}`}>{toast.message}</div>
      )}

      {addToLeadsOpen && selectedEmail && (
        <AddToLeadsModal
          contactName={parseAddress(selectedEmail.from).name || selectedEmail.from}
          customTouchpoints={customTouchpoints}
          adding={addingToLeads}
          linkedContact={linkedPreview}
          previewLoading={linkedPreviewLoading}
          onConfirm={handleConfirmAddToLeads}
          onClose={handleCloseAddToLeads}
        />
      )}

      <SafeGuardResendPopup
        session={resendSession}
        userId={userId}
        onEmailResult={handleResendResult}
        onClose={handleCloseResend}
      />

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          cancelLabel={confirm.cancelLabel}
          onConfirm={() => {
            const action = confirm.onConfirm;
            setConfirm(null);
            if (action) action();
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
